import * as THREE from "three";
import { signedArea } from "./signedArea";

const DEGENERATE_EPSILON = 1e-10;

/**
 * Unit normal that a positive offset distance pushes an edge along.
 *
 * For a ring this is the inward normal: the winding is normalised via the signed area, so
 * `+distance` always shrinks the shape and `-distance` always grows it, whether the vertices
 * were drawn clockwise or counter-clockwise.
 *
 * For an open chain there is no inside, so this is the right normal of the travel direction in
 * the (x, z) plane.
 */
function getEdgeNormal({ points, edgeIndex, isLoop }: { points: ReadonlyArray<THREE.Vector3>; edgeIndex: number; isLoop: boolean }): THREE.Vector3 | null {
	const start = points[edgeIndex];
	const end = points[(edgeIndex + 1) % points.length];

	if (start === undefined || end === undefined) {
		return null;
	}

	const directionX = end.x - start.x;
	const directionZ = end.z - start.z;
	const length = Math.hypot(directionX, directionZ);

	if (length < DEGENERATE_EPSILON) {
		return null;
	}

	const unitX = directionX / length;
	const unitZ = directionZ / length;

	if (isLoop === false || signedArea({ points }) < 0) {
		return new THREE.Vector3(unitZ, 0, -unitX);
	}

	return new THREE.Vector3(-unitZ, 0, unitX);
}

export { getEdgeNormal };
